import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getReferralOffer, getReferralLink } from '@cobu/api-client';
import type { ReferralOffer } from '@cobu/types';
import { useSelector } from 'react-redux';
import { Strings } from '../constants/Strings';
import { showErrorMessage, showSuccessMessage } from '../utils/messageToast';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { logger } from '../utils/logger';

// Constants
const REFERRAL_CONFIG_INJECTED_JAVASCRIPT = `(function() {
  window.isAppReferral = true;
})();`;

interface UseReferralDetailReturn {
  // Data
  referralOffer: ReferralOffer | null | undefined;
  referralLink: string | undefined;
  buildingName: string | undefined;
  primaryImageUri: string | undefined;
  // States  
  isLoading: boolean;
  isSharing: boolean;
  error: string | null;
  // Computed states
  canShare: boolean;
  canRefer: boolean;
  hasValidData: boolean;
  hasNoOffer: boolean;
  // Handlers
  handleShare: () => Promise<void>;
  handleReferNow: () => void;
  handleRetry: () => void;
}

const referralStrings = Strings.get().referral.detail;

export const useReferralDetail = (): UseReferralDetailReturn => {
  const navigate = useNavigate();
  const [isSharing, setIsSharing] = useState(false);

  // Get strings

  // Get user and building info from Redux state
  const user = useSelector((state: any) => state.auth.user);

  // Memoize user data to prevent unnecessary re-computations
  const userData = useMemo(() => ({
    buildingId: user?.building?.uuid,
    userId: user?.uuid,
    buildingName: user?.building?.name,
  }), [user?.building?.uuid, user?.uuid, user?.building?.name]);

  const { buildingId, userId, buildingName } = userData;

  // Fetch referral offer and link
  const {
    data: referralOffer,
    isLoading: isLoadingOffer,
    error: offerError
  } = useQuery({
    queryKey: ['referralOffer', buildingId || ''],
    queryFn: () => getReferralOffer(buildingId || ''),
    enabled: !!buildingId,
  });

  const {
    data: referralLink,
    isLoading: isLoadingLink,
    error: linkError
  } = useQuery({
    queryKey: ['referralLink', buildingId || '', userId || ''],
    queryFn: () => getReferralLink(buildingId || '', userId || ''),
    enabled: !!buildingId && !!userId,
  });

  // Computed states
  const isLoading = isLoadingOffer || isLoadingLink;
  const error = offerError?.message || linkError?.message || null;

  // Memoize computed validation states
  const computedStates = useMemo(() => ({
    canShare: !!(referralLink && referralOffer && buildingName && !isLoading),
    canRefer: !!(referralLink && !isLoading),
    hasValidData: !!(isLoadingOffer || (referralOffer && !error)),
    hasNoOffer: !isLoadingOffer && !offerError && referralOffer === null,
    primaryImageUri: referralOffer?.images?.[0] || referralOffer?.thumbnailImage || undefined,
  }), [referralLink, referralOffer, buildingName, isLoading, isLoadingOffer, error, offerError]);

  const { canShare, canRefer, hasValidData, hasNoOffer, primaryImageUri } = computedStates;

  // Memoize share message
  const shareMessage = useMemo(() => {
    if (!buildingName || !referralOffer) return '';
    return Strings.format(referralStrings.shareMessage, { buildingName });
  }, [buildingName, referralOffer, referralStrings.shareMessage]);

  const handleShare = useCallback(async () => {
    if (!canShare) {
      showErrorMessage(`${referralStrings.alertTitle}: ${referralStrings.errors.shareError}`);
      return;
    }

    setIsSharing(true);
    try {
      const absoluteUrl = (() => {
        if (!referralLink) return '';
        try {
          // Will succeed if referralLink is already absolute
          // eslint-disable-next-line no-new
          new URL(referralLink);
          return referralLink;
        } catch {
          return `${window.location.origin}${referralLink.startsWith('/') ? '' : '/'}${referralLink}`;
        }
      })();

      const text = shareMessage || '';

      if (typeof navigator !== 'undefined' && 'share' in navigator) {
        try {
          await (navigator as any).share({ title: referralOffer?.title || referralStrings.shareTitle, text, url: absoluteUrl });
          return;
        } catch (err: any) {
          // If user cancels share, don't show an error
          const msg = String(err?.message || err || '');
          const isAbort = msg.toLowerCase().includes('abort');
          if (isAbort) return;
          // fall through to clipboard
        }
      }

      const content = `${text}\n${absoluteUrl}`.trim();

      // Clipboard API (secure contexts only)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(content);
        const copiedText = referralStrings.success.shareCopied;
        showSuccessMessage(copiedText);
        return;
      }

      // Fallback: temporary textarea
      const textarea = document.createElement('textarea');
      textarea.value = content;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        const copiedText = referralStrings.success.shareCopied;
        showSuccessMessage(copiedText);
      } finally {
        document.body.removeChild(textarea);
      }
    } catch (error) {
      logger.error('Error sharing:', error);
      showErrorMessage(`${referralStrings.alertTitle}: ${referralStrings.errors.shareFailure}`);
    } finally {
      setIsSharing(false);
    }
  }, [canShare, referralOffer?.title, shareMessage, referralLink, referralStrings.alertTitle, referralStrings.errors.shareError, referralStrings.errors.shareFailure]);

  const handleReferNow = useCallback(() => {
    if (!canRefer) {
      showErrorMessage(`${referralStrings.alertTitle}: ${referralStrings.errors.referFormError}`);
      return;
    }

    // Navigate to WebView for the referral form
    const referralUrl = new URL(referralLink)
    referralUrl.searchParams.set('host', 'homebody')
    // The referral page renders the landing page (meant for the referred friend)
    // unless it is told this is an in-app referral. Native sets window.isAppReferral
    // by injecting JS into its WebView; on web the page is a cross-origin iframe,
    // so it reads this query param instead.
    referralUrl.searchParams.set('isAppReferral', 'true')
    navigate('/webview', {
      state: {
        url: referralUrl.toString(),
        title: referralStrings.webViewTitle,
        injectedJavaScript: REFERRAL_CONFIG_INJECTED_JAVASCRIPT
      }
    });
    analyticsService.trackEvent(AnalyticsEvent.REFERRAL_REFER_NOW, {
      referralOfferUuid: referralOffer?.uuid,
      buildingId,
    });
  }, [canRefer, referralLink, navigate, referralStrings.webViewTitle, referralStrings.alertTitle, referralStrings.errors.referFormError]);

  const handleRetry = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return {
    // Data
    referralOffer,
    referralLink,
    buildingName,
    primaryImageUri,
    // States
    isLoading,
    isSharing,
    error,
    // Computed states
    canShare,
    canRefer,
    hasValidData,
    hasNoOffer,
    // Handlers
    handleShare,
    handleReferNow,
    handleRetry,
  };
}; 
